/**
 * @fileoverview Calendar Grid Component
 * @description Month/Week/Day calendar grid with Teams-like merged event blocks
 */

import React, { useMemo } from 'react';
import { Box, Typography, Tooltip, Chip } from '@mui/material';
import {
  getMonthGrid, getWeekDays, getEventsForDay,
  isSameDay, isSameMonth, format, computeEventSpans,
} from '../../utils';
import { EVENT_STATUS_COLORS } from '../../constants';
import './CalendarGrid.css';
import { alpha } from '@mui/material/styles';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarGrid = ({ currentDate, view, events, onDayClick, onEventClick }) => {
  const days = view === 'week' ? getWeekDays(currentDate) : getMonthGrid(currentDate);
  const today = new Date();
  const maxVisibleRows = view === 'week' ? 5 : 3;

  // Compute merged event spans across consecutive days
  const { spans, overflowByDay } = useMemo(
    () => computeEventSpans(events, days, maxVisibleRows),
    [events, days, maxVisibleRows]
  );

  // Group spans by week index for rendering
  const spansByWeek = useMemo(() => {
    const grouped = {};
    spans.forEach((span) => {
      if (!grouped[span.weekIndex]) {
        grouped[span.weekIndex] = [];
      }
      grouped[span.weekIndex].push(span);
    });
    return grouped;
  }, [spans]);

  const totalWeeks = Math.ceil(days.length / 7);

  if (view === 'day') {
    return <DayView date={currentDate} events={events} onEventClick={onEventClick} />;
  }

  return (
    <Box className="calendar-grid">
      {/* Day Headers */}
      <Box className="calendar-header-row">
        {DAY_NAMES.map((name) => (
          <Box key={name} className="calendar-header-cell">
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#4B5563', textTransform: 'uppercase', fontSize: '0.7rem' }}>
              {name}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Body - Week Rows */}
      <Box className={`calendar-body-weeks ${view === 'week' ? 'week-view' : 'month-view'}`}>
        {Array.from({ length: totalWeeks }).map((_, weekIndex) => {
          const weekDays = days.slice(weekIndex * 7, (weekIndex + 1) * 7);
          const weekSpans = spansByWeek[weekIndex] || [];

          return (
            <Box key={weekIndex} className="calendar-week-row">
              {/* Day cells grid (shows day numbers and overflow) */}
              <Box className="calendar-days-grid">
                {weekDays.map((day, dayIndex) => {
                  const isToday = isSameDay(day, today);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const dayKey = format(day, 'yyyy-MM-dd');
                  const overflow = overflowByDay[dayKey] || 0;

                  return (
                    <Box
                      key={dayIndex}
                      className={`calendar-cell ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
                      onClick={() => onDayClick(day)}
                    >
                      <Typography
                        className={`day-number ${isToday ? 'today-badge' : ''}`}
                        variant="caption"
                      >
                        {format(day, 'd')}
                      </Typography>

                      {/* Spacer for event rows */}
                      <Box sx={{ flex: 1, minHeight: `${maxVisibleRows * 22}px` }} />

                      {/* Overflow indicator */}
                      {overflow > 0 && (
                        <Typography variant="caption" className="more-events" sx={{ fontSize: '0.6rem', color: '#6B7280', pl: 0.5 }}>
                          +{overflow} more
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Events overlay - spans across days */}
              <Box className="calendar-events-overlay">
                {weekSpans.map((span) => {
                  const { event, rowIndex, startCol, endCol, spanDays } = span;
                  // Use the event's own color - display_color or color_code take priority
                  const eventColor = event.display_color || event.color_code || EVENT_STATUS_COLORS[event.status] || '#4A90D9';
                  const color = event.display_color || event.color_code || '#FF8A65';
                  return (
                    <Tooltip
                      key={`event-${event.id}-week-${weekIndex}`}
                      title={`${event.title} • ${event.status} • ${event.team_name || ''} • ${spanDays} day${spanDays > 1 ? 's' : ''}`}
                      arrow
                      placement="top"
                    >
                      <Box
                              className="event-span"
                              sx={{
                                  gridColumn: `${startCol} / ${endCol}`,
                                  gridRow: rowIndex + 1,

                                  backgroundColor: `${color}18`,        // Light background (approx 0.094 opacity)
                                  borderLeft: `3px solid ${color}`,
                                  color: color,                         // Colored text

                                  padding: '3px 8px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  overflow: 'hidden',
                              }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event);
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {event.title}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Day View — shows a single day's events as a list
 */
const DayView = ({ date, events, onEventClick }) => {
  const dayEvents = getEventsForDay(events, date);

  return (
    <Box sx={{ p: 2 }}>
      {dayEvents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>
          <Typography variant="body1">No events scheduled for this day</Typography>
        </Box>
      ) : (
        dayEvents.map((evt) => {
          const color = evt.display_color || evt.color_code || EVENT_STATUS_COLORS[evt.status] || '#4A90D9';
          return (
            <Box
              key={evt.id}
              onClick={() => onEventClick(evt)}
              sx={{
                p: 2,
                mb: 1.5,
                borderRadius: 2,
                backgroundColor: `${color}0D`,
                borderLeft: `4px solid ${color}`,
                cursor: 'pointer',
                transition: 'transform 0.1s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateX(4px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{evt.title}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip label={evt.status} size="small" sx={{ backgroundColor: color, color: '#fff', fontSize: '0.65rem', height: 20 }} />
                  {evt.priority && (
                    <Chip label={evt.priority} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                  )}
                </Box>
              </Box>
              {evt.description && (
                <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mb: 0.5 }}>
                  {evt.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {evt.team_name && (
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>👥 {evt.team_name}</Typography>
                )}
                {evt.test_type && (
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>🧪 {evt.test_type}</Typography>
                )}
                {evt.environment_name && (
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>🌐 {evt.environment_name}</Typography>
                )}
              </Box>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export { CalendarGrid };
export default CalendarGrid;
